import React from 'react'
import _ from 'lodash'
import classnames from 'classnames/bind'
import { useApplicationTheme } from '../../applicationTheme'
import { getThemedCompact } from '../util'
import { FormFieldCommonProps } from '../../../types'
import Button from '../../elementals/button'
import Icon from '../../elementals/icon'
import MaskedInputField from '../maskedInputField'
import NumberMask from '../../../utils/numberMask'
import styles from './styles.scss'
// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const cx = classnames.bind(styles)

interface BaseNumberInputFieldProps extends FormFieldCommonProps {
  label: string | React.ReactNode
  name: string
  compact?: boolean
  maskOptions?: {
    allowNegative?: boolean
    [key: string]: string | boolean | number | undefined
  }
  showIncrementButtons?: boolean
  incrementBy?: number
  value?: string
  buttons?: {
    increment?: {
      elmId: string // Required string
    }
    decrement?: {
      elmId: string // Required string
    }
  }
}

const BaseNumberInputField: React.FC<BaseNumberInputFieldProps> = props => {
  const {
    maskOptions,
    showIncrementButtons,
    incrementBy = 1,
    onChange,
    value,
    buttons,
  } = props
  const numberMask = new NumberMask(maskOptions)
  const theme = useApplicationTheme()
  const newCompact = getThemedCompact(theme, props.compact)

  // When number has more than 3 digits, value is 1,234 as string
  // When number has less than 3 digits, value is 123 as number
  const increment = (up: boolean) => {
    const asNumber = value ? `${value}`.replace(/,/g, '') : '0'
    const currentValue = parseFloat(asNumber)
    const newValue = up
      ? currentValue + incrementBy
      : currentValue - incrementBy
    if (newValue < 0 && !maskOptions?.allowNegative) return // do nothing if negative value is not allowed
    const normalizedValue: string = normalizeOnChange(`${newValue}`)
    if (onChange) {
      const syntheticEvent = {
        target: {
          value: normalizedValue,
        },
      } as React.ChangeEvent<HTMLInputElement>
      const meta = { value: normalizedValue }
      onChange(syntheticEvent, meta) // eslint-disable-line @typescript-eslint/no-unsafe-call
    }
  }

  const normalizeOnChange = (s: string): string => {
    return numberMask.normalize(s, false) // eslint-disable-line
  }

  const normalizeOnBlur = (s: string): string => {
    return numberMask.normalize(s, true) // eslint-disable-line
  }

  return (
    <MaskedInputField
      inputMode='numeric'
      mask={numberMask.mask} // eslint-disable-line
      normalizeOnChange={normalizeOnChange}
      normalizeOnBlur={normalizeOnBlur}
      {..._.omit(props, ['maskOptions', 'showIncrementButtons'])}
    >
      <input
        className={cx({ 'input-with-buttons-on-right': showIncrementButtons })}
      />
      {showIncrementButtons && (
        <Button
          style={newCompact ? { minHeight: '38px' } : {}}
          elmId={`${buttons?.decrement?.elmId ?? 'decrement'}_button`}
          collapse
          className={cx('minus-button')}
          onClick={() => increment(false)}
        >
          <Icon name='minus' iconStyle='light' />
        </Button>
      )}
      {showIncrementButtons && (
        <Button
          style={newCompact ? { minHeight: '38px' } : {}}
          elmId={`${buttons?.increment?.elmId ?? 'increment'}_button`}
          collapse
          className={cx('plus-button')}
          onClick={() => increment(true)}
        >
          <Icon name='plus' iconStyle='light' />
        </Button>
      )}
    </MaskedInputField>
  )
}

export default BaseNumberInputField
